import type { ProjectSummary } from "@archinventory/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronRight, MapPin, Plus, Search } from "lucide-react";
import { useState } from "react";
import { Empty, Page } from "../../../components/page";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Dialog } from "../../../components/ui/dialog";
import { Input, Label, Select } from "../../../components/ui/input";
import { Segmented } from "../../../components/ui/segmented";
import { api, qk, unwrap } from "../../../lib/api";
import { toastError, useCan } from "../../../lib/hooks";
import { cn } from "../../../lib/utils";

export const Route = createFileRoute("/_app/projects/")({ component: Projects });

type Show = "active" | "archived" | "all";

function Projects() {
  const canManage = useCan("admin");
  const [creating, setCreating] = useState(false);
  const [show, setShow] = useState<Show>("active");
  const [filter, setFilter] = useState("");
  const q = useQuery({ queryKey: qk.projects, queryFn: () => unwrap(api.projects.$get()) });

  const projects = q.data?.projects ?? [];
  const active = projects.filter((p) => p.status === "active");
  const archivedCount = projects.length - active.length;
  const needle = filter.trim().toLowerCase();
  const visible = projects
    .filter((p) => show === "all" || p.status === show)
    .filter(
      (p) =>
        !needle ||
        [p.name, p.clientName, p.siteAddress ?? ""].some((v) => v.toLowerCase().includes(needle)),
    );
  const byClient = new Map<string, ProjectSummary[]>();
  for (const p of visible) byClient.set(p.clientName, [...(byClient.get(p.clientName) ?? []), p]);
  const sum = (k: "itemCount" | "toReviewCount") => active.reduce((n, p) => n + p[k], 0);

  return (
    <Page
      title="Projects"
      subtitle={
        q.data &&
        [
          plural(active.length, "active project"),
          plural(sum("itemCount"), "item"),
          sum("toReviewCount") > 0 && `${sum("toReviewCount")} to review`,
        ]
          .filter(Boolean)
          .join(" · ")
      }
      actions={
        canManage && (
          <Button onClick={() => setCreating(true)}>
            <Plus /> New project
          </Button>
        )
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Segmented
          label="Show projects"
          value={show}
          onChange={setShow}
          options={[
            { value: "active", label: "Active", count: active.length },
            { value: "archived", label: "Archived", count: archivedCount },
            { value: "all", label: "All" },
          ]}
        />
        {projects.length > 6 && (
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Filter projects"
              placeholder="Filter projects…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-8"
            />
          </div>
        )}
      </div>

      {q.data && visible.length === 0 && (
        <Empty>
          {needle ? (
            "No project matches the filter."
          ) : show === "archived" ? (
            "No archived projects."
          ) : (
            <div className="flex flex-col items-center gap-3">
              <span>No projects yet.</span>
              {canManage && (
                <Button variant="outline" onClick={() => setCreating(true)}>
                  <Plus /> Create project
                </Button>
              )}
            </div>
          )}
        </Empty>
      )}
      {[...byClient.entries()].map(([client, list]) => (
        <section key={client} className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              {client}
            </h2>
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">{plural(list.length, "project")}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((p) => (
              <ProjectCard key={p.id} p={p} />
            ))}
          </div>
        </section>
      ))}
      <NewProjectDialog open={creating} onClose={() => setCreating(false)} />
    </Page>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

function ProjectCard({ p }: { p: ProjectSummary }) {
  const archived = p.status === "archived";
  return (
    <Link to="/projects/$projectId" params={{ projectId: p.id }} className="group">
      <Card
        className={cn(
          "flex h-full flex-col gap-3 p-4 transition-all group-hover:border-primary/40 group-hover:shadow-sm",
          archived && "bg-muted/40",
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-col gap-1">
            <span className={cn("truncate font-medium", archived && "text-muted-foreground")}>
              {p.name}
            </span>
            {p.siteAddress && (
              <span className="flex items-center gap-1 truncate text-sm text-muted-foreground">
                <MapPin className="size-3.5 shrink-0" /> {p.siteAddress}
              </span>
            )}
          </div>
          {archived ? (
            <Badge variant="secondary">Archived</Badge>
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          )}
        </div>
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="text-muted-foreground">{plural(p.itemCount, "item")}</span>
          {p.toReviewCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-warning" /> {p.toReviewCount} to review
            </span>
          )}
          {p.inProgressCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="size-2 animate-pulse rounded-full bg-primary" /> {p.inProgressCount}{" "}
              in progress
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
}

function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const clients = useQuery({
    queryKey: qk.clients,
    queryFn: () => unwrap(api.clients.$get()),
    enabled: open,
  });
  const create = useMutation({
    mutationFn: (v: { clientId: string; name: string; siteAddress: string }) =>
      unwrap(api.projects.$post({ json: v })),
    onSuccess: async (r) => {
      onClose();
      await qc.invalidateQueries({ queryKey: qk.projects });
      if (r.project)
        await navigate({ to: "/projects/$projectId", params: { projectId: r.project.id } });
    },
    onError: toastError,
  });
  return (
    <Dialog open={open} onClose={onClose} title="New project">
      {clients.data?.clients.length === 0 ? (
        <p className="text-sm">
          Create a{" "}
          <Link to="/clients" className="text-primary underline">
            client
          </Link>{" "}
          first.
        </p>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            create.mutate({
              clientId: String(f.get("clientId")),
              name: String(f.get("name")),
              siteAddress: String(f.get("siteAddress") ?? ""),
            });
          }}
        >
          <Label htmlFor="p-client">Client</Label>
          <Select id="p-client" name="clientId" required>
            {clients.data?.clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Label htmlFor="p-name">Project name</Label>
          <Input id="p-name" name="name" placeholder="e.g. HQ Berlin, 3rd floor" required />
          <Label htmlFor="p-site">Site address</Label>
          <Input id="p-site" name="siteAddress" />
          <Button type="submit" disabled={create.isPending}>
            Create
          </Button>
        </form>
      )}
    </Dialog>
  );
}
